import { z } from 'zod';

export const TransformSchema = z.object({
  translation: z.tuple([z.number(), z.number(), z.number()]),
  rotation: z.tuple([z.number(), z.number(), z.number(), z.number()]),
  scale: z.tuple([z.number(), z.number(), z.number()]),
});

export type Transform = z.infer<typeof TransformSchema>;

const BaseNodeSchema = z.object({
  id: z.string(),
  transform: TransformSchema,
});

const ObjectNodeSchema = BaseNodeSchema.extend({
  type: z.literal('object'),
  asset_ref: z.object({
    pid: z.string(),
    url: z.string(),
  }),
});

const CameraRigNodeSchema = BaseNodeSchema.extend({
  type: z.literal('camera_rig'),
  role: z.string().optional(),
  rig: z.object({
    mode: z.enum(['orbit', 'free']), // Add other modes if known
    target: z.string(),
    radius: z.number(),
    height: z.number(),
    azimuth_range: z.tuple([z.number(), z.number()]),
    polar_angle: z.number(),
  }),
});

const CameraTargetNodeSchema = BaseNodeSchema.extend({
  type: z.literal('camera_target'),
});

const SplatNodeSchema = BaseNodeSchema.extend({
  type: z.literal('splat'),
});

const CameraPlaneNodeSchema = BaseNodeSchema.extend({
  type: z.literal('camera_plane'),
  world_transform: TransformSchema,
  dimensions: z.object({
    width: z.number(),
    height: z.number(),
  }),
  padding: z.object({
    top: z.number(),
    bottom: z.number(),
    left: z.number(),
    right: z.number(),
  }),
});

export const SceneNodeUnion = z.discriminatedUnion('type', [
  ObjectNodeSchema,
  CameraRigNodeSchema,
  CameraTargetNodeSchema,
  SplatNodeSchema,
  CameraPlaneNodeSchema,
]);

export type ObjectNode = z.infer<typeof ObjectNodeSchema>;
export type CameraRigNode = z.infer<typeof CameraRigNodeSchema>;
export type CameraTargetNode = z.infer<typeof CameraTargetNodeSchema>;
export type SplatNode = z.infer<typeof SplatNodeSchema>;
export type CameraPlaneNode = z.infer<typeof CameraPlaneNodeSchema>;

export type SceneNode = z.infer<typeof SceneNodeUnion> & {
  children?: SceneNode[];
};

export const SceneNodeSchema: z.ZodType<SceneNode> = SceneNodeUnion.and(
  z.object({
    children: z.array(z.lazy(() => SceneNodeSchema)).optional(),
  })
);

export const SceneRootSchema = z.object({
  nodes: z.array(SceneNodeSchema),
});

export const SceneDataSchema = z.object({
  schema: z.string(),
  scene_id: z.string(),
  active_camera_id: z.string(),
  units: z.object({
    length: z.string(),
    angle: z.string(),
  }),
  coordinate_system: z.object({
    handedness: z.string(),
    up_axis: z.string(),
    forward_axis: z.string(),
  }),
  root: SceneRootSchema,
});

export type SceneData = z.infer<typeof SceneDataSchema>;

